/**
 * Shopify Collection Service
 * 
 * Handles all collection-related operations including assignments,
 * updates, and queries according to Shopify Admin API 2025-01
 * specifications.
 */

export class ShopifyCollectionService {
  constructor(graphqlService) {
    this.graphqlService = graphqlService;
  }

  /**
   * Assigns a product to collections
   * @param {string} productId - The product ID
   * @param {Array<string>} collectionIds - Collection IDs to assign
   * @returns {Promise<object>} Result of the collection assignments
   */
  async assignToCollections(productId, collectionIds) {
    if (!Array.isArray(collectionIds) || collectionIds.length === 0) {
      return { success: true, collections: [] };
    }

    const addToCollectionsMutation = `
      mutation collectionAddProducts($id: ID!, $productIds: [ID!]!) {
        collectionAddProducts(id: $id, productIds: [$productId]) {
          collection {
            id
            title
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const results = [];
    for (const collectionId of collectionIds) {
      const result = await this.graphqlService.mutate(addToCollectionsMutation, {
        id: collectionId,
        productIds: [productId]
      });

      if (result?.collectionAddProducts?.userErrors?.length > 0) {
        console.error(`Failed to add product to collection ${collectionId}:`, result.collectionAddProducts.userErrors);
      } else {
        results.push(result?.collectionAddProducts?.collection);
      }
    }

    return {
      success: true,
      collections: results
    };
  }

  /**
   * Removes a product from collections
   * @param {string} productId - The product ID
   * @param {Array<string>} collectionIds - Collection IDs to remove from
   * @returns {Promise<object>} Result of the collection removals
   */
  async removeFromCollections(productId, collectionIds) {
    if (!Array.isArray(collectionIds) || collectionIds.length === 0) {
      return { success: true, collections: [] };
    }

    const removeFromCollectionsMutation = `
      mutation collectionRemoveProducts($id: ID!, $productIds: [ID!]!) {
        collectionRemoveProducts(id: $id, productIds: [$productId]) {
          collection {
            id
            title
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const results = [];
    for (const collectionId of collectionIds) {
      const result = await this.graphqlService.mutate(removeFromCollectionsMutation, {
        id: collectionId,
        productIds: [productId]
      });

      if (result?.collectionRemoveProducts?.userErrors?.length > 0) {
        console.error(`Failed to remove product from collection ${collectionId}:`, result.collectionRemoveProducts.userErrors);
      } else {
        results.push(result?.collectionRemoveProducts?.collection);
      }
    }

    return {
      success: true,
      collections: results
    };
  }

  /**
   * Gets collections that a product belongs to
   * @param {string} productId - The product ID
   * @returns {Promise<Array>} List of collections
   */
  async getProductCollections(productId) {
    const productCollectionsQuery = `
      query getProductCollections($productId: ID!) {
        product(id: $productId) {
          collections(first: 50) {
            edges {
              node {
                id
                title
                handle
                productsCount
              }
            }
          }
        }
      }
    `;

    const result = await this.graphqlService.query(productCollectionsQuery, { productId });
    return result?.product?.collections?.edges?.map(edge => edge.node) || [];
  }

  /**
   * Gets available collections for product assignment
   * @param {number} limit - Maximum number of collections to fetch
   * @returns {Promise<Array>} List of available collections
   */
  async getAvailableCollections(limit = 50) {
    const collectionsQuery = `
      query getCollections($first: Int!) {
        collections(first: $first) {
          edges {
            node {
              id
              title
              handle
              productsCount
              ruleSet {
                appliedDisjunctively
                rules {
                  column
                  condition
                  relation
                }
              }
            }
          }
        }
      }
    `;

    const result = await this.graphqlService.query(collectionsQuery, { first: limit });
    return result?.collections?.edges?.map(edge => edge.node) || [];
  }

  /**
   * Updates collection assignments for a product
   * @param {string} productId - The product ID
   * @param {Array<string>} addToCollectionIds - Collections to add to
   * @param {Array<string>} removeFromCollectionIds - Collections to remove from
   * @returns {Promise<object>} Result of the collection updates
   */
  async updateCollectionAssignments(productId, addToCollectionIds = [], removeFromCollectionIds = []) {
    const results = {
      added: [],
      removed: [],
      errors: []
    };

    // Remove from collections first
    if (removeFromCollectionIds.length > 0) {
      const removeResult = await this.removeFromCollections(productId, removeFromCollectionIds);
      if (removeResult.collections) {
        results.removed = removeResult.collections;
      }
    }

    // Then add to new collections
    if (addToCollectionIds.length > 0) {
      const addResult = await this.assignToCollections(productId, addToCollectionIds);
      if (addResult.collections) {
        results.added = addResult.collections;
      }
    }

    return results;
  }

  /**
   * Checks if a collection is automated (has rules)
   * @param {object} collection - Collection data
   * @returns {boolean} True if collection is automated
   */
  isAutomatedCollection(collection) {
    return collection.ruleSet && 
           Array.isArray(collection.ruleSet.rules) && 
           collection.ruleSet.rules.length > 0;
  }

  /**
   * Validates collection assignments
   * @param {Array<string>} collectionIds - Collection IDs to validate
   * @returns {Promise<object>} Validation result
   */
  async validateCollectionAssignments(collectionIds) {
    if (!Array.isArray(collectionIds)) {
      return {
        isValid: false,
        errors: ['Collection IDs must be an array']
      };
    }

    const errors = [];
    const collections = await this.getAvailableCollections();
    const validIds = new Set(collections.map(c => c.id));

    // Check each ID is valid
    collectionIds.forEach(id => {
      if (!validIds.has(id)) {
        errors.push(`Invalid collection ID: ${id}`);
      }
    });

    // Check for automated collections
    const automatedCollections = collections
      .filter(c => validIds.has(c.id) && this.isAutomatedCollection(c));

    automatedCollections.forEach(c => {
      errors.push(`Cannot manually assign to automated collection: ${c.title}`);
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}
